import { Plus } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Select } from "@/components/base/select/select";
import type { QuestionSettings } from "@/types/question";
import { QuestionOptionDisplayType, QuestionType } from "@/types/question";
import type {
	QuestionOption,
	QuestionTypeOrChecklist,
} from "../../hooks/useQuestionForm";
import { OptionsEditor } from "../OptionsEditor";

type OptionsBlockProps = {
	selectedType: QuestionTypeOrChecklist;
	settings: QuestionSettings;
	setSettings: (settings: QuestionSettings) => void;
	options: QuestionOption[];
	onAddOption: () => void;
	onUpdateOption: (id: string, text: string) => void;
	onDeleteOption: (id: string) => void;
	onReorderOptions?: (fromIndex: number, toIndex: number) => void;
};

export const OptionsBlock = ({
	selectedType,
	settings,
	setSettings,
	options,
	onAddOption,
	onUpdateOption,
	onDeleteOption,
	onReorderOptions,
}: OptionsBlockProps) => (
	<div>
		<div className="mb-4 flex items-center justify-between">
			<h3 className="font-semibold text-gray-900 text-lg">
				{selectedType === 999
					? "Opções de Checklist Padrão"
					: "Opções de resposta"}
			</h3>
			{selectedType === QuestionType.Option && (
				<Button
					color="secondary"
					iconLeading={Plus}
					onClick={onAddOption}
					size="sm"
				>
					Adicionar
				</Button>
			)}
		</div>

		{selectedType === QuestionType.Option && (
			<div className="mb-6 space-y-4">
				<div>
					<label
						className="mb-2 block font-medium text-gray-700 text-sm"
						htmlFor="option-display-type"
					>
						Tipo de exibição
					</label>
					<Select
						id="option-display-type"
						onSelectionChange={(key) => {
							const newDisplayType = Number(key) as QuestionOptionDisplayType;
							if (
								newDisplayType === QuestionOptionDisplayType.Radio ||
								newDisplayType === QuestionOptionDisplayType.ComboBox
							) {
								setSettings({
									...settings,
									optionDisplayType: newDisplayType,
									isMultipleSelection: false,
								});
							} else {
								setSettings({
									...settings,
									optionDisplayType: newDisplayType,
								});
							}
						}}
						placeholder="Selecione o tipo de exibição"
						selectedKey={
							settings.optionDisplayType?.toString() ??
							QuestionOptionDisplayType.Checkbox.toString()
						}
					>
						<Select.Item id={QuestionOptionDisplayType.Checkbox.toString()}>
							Caixa de seleção
						</Select.Item>
						{settings.optionDisplayType === QuestionOptionDisplayType.Radio && (
							<Select.Item id={QuestionOptionDisplayType.Radio.toString()}>
								Seleção única
							</Select.Item>
						)}
						<Select.Item id={QuestionOptionDisplayType.ComboBox.toString()}>
							Menu suspenso
						</Select.Item>
						<Select.Item id={QuestionOptionDisplayType.List.toString()}>
							Lista
						</Select.Item>
					</Select>
				</div>

				{(settings.optionDisplayType === QuestionOptionDisplayType.Checkbox ||
					settings.optionDisplayType === QuestionOptionDisplayType.List) && (
					<div>
						<label className="mb-2 flex items-center gap-2">
							<input
								checked={settings.isMultipleSelection ?? false}
								className="h-4 w-4 rounded border-gray-300 text-blue-600 accent-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
								onChange={(e) =>
									setSettings({
										...settings,
										isMultipleSelection: e.target.checked,
									})
								}
								style={{ accentColor: "#2563eb" }}
								type="checkbox"
							/>
							<span className="font-medium text-gray-700 text-sm">
								Permitir múltipla escolha
							</span>
						</label>
					</div>
				)}
			</div>
		)}

		<OptionsEditor
			onDelete={onDeleteOption}
			onReorder={onReorderOptions}
			onUpdate={onUpdateOption}
			options={options}
			type={selectedType}
		/>
	</div>
);
